import { useRef, useCallback, useState, useEffect } from 'react';
import { Terminal as XTerminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { 
  TerminalState, 
  TerminalConfig, 
  TerminalMethods,
  WebSocketMessage,
  WebSocketResponse,
  DEFAULT_TERMINAL_THEME
} from '../components/terminal/types';
import { getWebSocketUrl } from '../utils/api';
import { terminalLogger } from '../utils/terminalLogger';

/**
 * Terminal hook with proper dependency management to avoid infinite loops
 * 
 * Key principles:
 * 1. ALL callbacks have NO dependencies []
 * 2. Config stored in ref to access current values without recreating functions
 * 3. All instance variables in refs (terminal, websocket, etc.)
 * 4. Single initialization guard prevents double initialization
 * 5. Proper cleanup on unmount
 */
export function useTerminal(config: TerminalConfig): {
  terminalRef: (node: HTMLDivElement | null) => void;
  state: TerminalState;
  methods: TerminalMethods;
} {
  // State - only for UI updates
  const [state, setState] = useState<TerminalState>({ type: 'initializing' });
  
  // Store config in ref to access current values without dependencies
  const configRef = useRef(config);
  configRef.current = config; // Update on each render
  
  // Refs for all instance variables - stable across renders
  const terminalRef = useRef<XTerminal | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const webSocketRef = useRef<WebSocket | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const inputBufferRef = useRef<string>('');
  const flushTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initializedRef = useRef(false);
  
  // Handler refs - update on each render to avoid stale closures
  const handleDataRef = useRef<((data: string) => void) | null>(null);
  const handleKeyEventRef = useRef<((event: KeyboardEvent) => boolean) | null>(null);
  
  // Heartbeat refs
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastPingTimestampRef = useRef<number>(0);
  const connectionLatencyRef = useRef<number>(0);
  
  /**
   * Flush input buffer - NO dependencies
   */
  const flushInputBuffer = useCallback(() => {
    if (inputBufferRef.current && webSocketRef.current?.readyState === WebSocket.OPEN) {
      const message: WebSocketMessage = { 
        type: 'input', 
        data: inputBufferRef.current 
      };
      terminalLogger.debug('Sending input message', configRef.current.sessionId, { messageLength: message.data?.length || 0 });
      webSocketRef.current.send(JSON.stringify(message));
      inputBufferRef.current = '';
    } else {
      terminalLogger.warn('Cannot send - buffer empty or WebSocket not open', configRef.current.sessionId, { readyState: webSocketRef.current?.readyState });
    }
    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current);
      flushTimeoutRef.current = null;
    }
  }, []); // NO dependencies!

  /**
   * Send WebSocket message - NO dependencies
   */
  const sendWebSocketMessage = useCallback((message: WebSocketMessage) => {
    if (webSocketRef.current?.readyState === WebSocket.OPEN) {
      webSocketRef.current.send(JSON.stringify(message));
    }
  }, []); // NO dependencies!

  /**
   * Start heartbeat - NO dependencies
   */
  const startHeartbeat = useCallback(() => {
    // Clear any existing intervals
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
    }

    // Send ping every 30 seconds
    heartbeatIntervalRef.current = setInterval(() => {
      if (webSocketRef.current?.readyState === WebSocket.OPEN) {
        const timestamp = Date.now();
        lastPingTimestampRef.current = timestamp;
        
        const pingMessage = { type: 'ping' as const, timestamp };
        sendWebSocketMessage(pingMessage);
        terminalLogger.info('Heartbeat ping sent', configRef.current.sessionId, pingMessage);
        
        // Expect pong within 10 seconds (more forgiving)
        heartbeatTimeoutRef.current = setTimeout(() => {
          terminalLogger.warn('Heartbeat timeout - no pong received', configRef.current.sessionId);
          // Reconnect if no pong received
          if (webSocketRef.current?.readyState === WebSocket.OPEN) {
            webSocketRef.current.close();
          }
        }, 10000);
      }
    }, 30000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // NO dependencies!

  /**
   * Stop heartbeat - NO dependencies
   */
  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }
  }, []); // NO dependencies!

  /**
   * Connect WebSocket - NO dependencies, uses configRef
   */
  const connectWebSocket = useCallback(() => {
    // Prevent duplicate connections
    if (webSocketRef.current?.readyState === WebSocket.CONNECTING || 
        webSocketRef.current?.readyState === WebSocket.OPEN) {
      terminalLogger.debug('Already connected or connecting, skipping new connection', configRef.current.sessionId);
      return;
    }
    
    // Close existing connection if any
    if (webSocketRef.current) {
      webSocketRef.current.close();
    }

    // Access current config from ref
    const currentConfig = configRef.current;
    
    terminalLogger.connection('connecting', currentConfig.sessionId);
    setState({ type: 'connecting' });
    
    const wsUrl = getWebSocketUrl(`/ws/terminal/${currentConfig.sessionId}`);
    const ws = new WebSocket(wsUrl);
    webSocketRef.current = ws;

    ws.onopen = () => {
      terminalLogger.connection('connected', currentConfig.sessionId);
      setState({ type: 'connected', sessionId: currentConfig.sessionId });
      reconnectAttemptsRef.current = 0;
      
      // Initialize terminal now that WebSocket is connected
      if (containerRef.current && !initializedRef.current) {
        initializeTerminal();
      }
      
      // Start heartbeat
      startHeartbeat();
    };

    ws.onmessage = (event) => {
      // Process WebSocket messages
      
      try {
        const response: WebSocketResponse = JSON.parse(event.data);
        terminalLogger.debug('WebSocket message received', configRef.current.sessionId, { type: response.type });
        
        // Handle pong messages
        if (response.type === 'pong') {
          terminalLogger.debug('Pong received', currentConfig.sessionId, { timestamp: (response as any).timestamp });
        }
        
        // Handle pong response
        if (response.type === 'pong') {
          if (heartbeatTimeoutRef.current) {
            clearTimeout(heartbeatTimeoutRef.current);
            heartbeatTimeoutRef.current = null;
          }
          
          // Calculate latency
          if (response.timestamp && lastPingTimestampRef.current) {
            const latency = Date.now() - lastPingTimestampRef.current;
            connectionLatencyRef.current = latency;
            terminalLogger.debug('Heartbeat pong received', currentConfig.sessionId, { latency });
          }
          return;
        }
        
        if (terminalRef.current) {
          switch (response.type) {
            case 'connected':
              if (response.message) {
                terminalRef.current.write('\x1b[32m✓ ' + response.message + '\x1b[0m\r\n\r\n');
              }
              break;
              
            case 'output':
              if (response.data) {
                terminalRef.current.write(response.data);
              }
              break;
              
            case 'error':
              if (response.message) {
                terminalRef.current.write('\x1b[31mError: ' + response.message + '\x1b[0m\r\n');
              }
              setState({ 
                type: 'error', 
                message: response.message || 'Unknown error', 
                retryable: true 
              });
              break;
          }
        }
      } catch (e) {
        // Not JSON, treat as raw output
        if (terminalRef.current) {
          terminalRef.current.write(event.data);
        }
      }
    };

    ws.onerror = (error) => {
      terminalLogger.websocket('error', currentConfig.sessionId, error);
      // Don't set terminal error state here — let onclose handle reconnection.
      // onerror always fires before onclose for connection failures, and setting
      // error state here would flash an error before the reconnect kicks in.
    };

    ws.onclose = () => {
      terminalLogger.connection('disconnected', currentConfig.sessionId);

      // Stop heartbeat
      stopHeartbeat();

      // Auto-reconnect with exponential backoff (handles both initial
      // connection failures and mid-session disconnects)
      if (reconnectAttemptsRef.current < 5) {
        reconnectAttemptsRef.current++;
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 10000);

        setState({ type: 'connecting' });

        if (terminalRef.current) {
          terminalRef.current.write(
            `\x1b[33mReconnecting in ${delay/1000}s... (attempt ${reconnectAttemptsRef.current}/5)\x1b[0m\r\n`
          );
        }

        // IMPORTANT: Use arrow function to capture current connectWebSocket
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, delay);
      } else {
        setState({ type: 'error', message: 'Connection failed after 5 attempts', retryable: true });

        if (terminalRef.current) {
          terminalRef.current.write('\r\n\x1b[31m⚠ Connection failed after 5 attempts\x1b[0m\r\n');
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // NO dependencies!

  /**
   * Initialize terminal - NO dependencies, uses refs
   */
  const initializeTerminal = useCallback(() => {
    const container = containerRef.current;
    if (!container || initializedRef.current || terminalRef.current) return;
    
    initializedRef.current = true;
    // Don't change state - we're already connected when this is called

    try {
      // Create terminal instance
      const terminal = new XTerminal({
        cursorBlink: true,
        fontSize: 14,
        fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", Monaco, Consolas, monospace',
        fontWeight: '400',
        fontWeightBold: '600',
        lineHeight: 1.4,
        letterSpacing: 0.5,
        convertEol: true,
        cols: 100,
        rows: 30,
        scrollback: 10000,
        theme: DEFAULT_TERMINAL_THEME,
        allowTransparency: true,
        windowsMode: false,
        macOptionIsMeta: true,
        allowProposedApi: true,
        scrollOnUserInput: false,
        fastScrollModifier: 'shift' as const
      });

      // Create addons
      const fitAddon = new FitAddon();
      const webLinksAddon = new WebLinksAddon();
      terminal.loadAddon(fitAddon);
      terminal.loadAddon(webLinksAddon);
      
      // Open terminal
      terminal.open(container);
      
      // Store references
      terminalRef.current = terminal;
      fitAddonRef.current = fitAddon;
      
      // Write connection info banner
      const currentConfig = configRef.current;
      terminal.write('\x1b[36m╭─ PropAlgos Terminal\x1b[0m\r\n');
      terminal.write(`\x1b[36m├─ Provider: \x1b[0m${currentConfig.provider?.toUpperCase() || 'Unknown'}\r\n`);
      if (currentConfig.instanceId) {
        terminal.write(`\x1b[36m├─ Instance: \x1b[0m${currentConfig.instanceId}\r\n`);
      }
      if (currentConfig.instanceType) {
        terminal.write(`\x1b[36m├─ Type: \x1b[0m${currentConfig.instanceType}\r\n`);
      }
      if (currentConfig.region) {
        terminal.write(`\x1b[36m├─ Region: \x1b[0m${currentConfig.region}\r\n`);
      }
      terminal.write('\x1b[36m├─ Status: \x1b[32mConnected\x1b[0m\r\n');
      terminal.write('\x1b[36m╰─ Ready for commands\x1b[0m\r\n\r\n');
      
      // Initial fit with delay
      setTimeout(() => {
        if (fitAddonRef.current) {
          try {
            fitAddonRef.current.fit();
            terminal.focus();
            
            // Extra focus for standalone mode
            if (configRef.current.standalone) {
              // Try focus multiple times for standalone terminals
              const focusAttempts = [100, 300, 500];
              focusAttempts.forEach(delay => {
                setTimeout(() => {
                  if (terminalRef.current) {
                    terminalRef.current.focus();
                  }
                }, delay);
              });
            }
          } catch (e) {
            terminalLogger.warn('Terminal fit failed', configRef.current.sessionId, e);
          }
        }
      }, 50);

      // Setup input handler - uses ref to avoid stale closures
      terminal.onData((data: string) => {
        handleDataRef.current?.(data);
      });
      
      // Setup keyboard shortcuts - uses ref to avoid stale closures
      terminal.attachCustomKeyEventHandler((event: KeyboardEvent) => {
        return handleKeyEventRef.current?.(event) ?? true;
      });

      // WebSocket connection is handled separately now
      
    } catch (error) {
      terminalLogger.error('Failed to initialize terminal', configRef.current.sessionId, error);
      setState({ type: 'error', message: 'Failed to initialize terminal', retryable: true });
      initializedRef.current = false;
    }
  }, []); // NO dependencies!

  /**
   * Container ref callback - NO dependencies
   */
  const containerRefCallback = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;
    // If WebSocket is already connected and we just got the container, initialize now
    if (node && !initializedRef.current && webSocketRef.current?.readyState === WebSocket.OPEN) {
      initializeTerminal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // NO dependencies!

  /**
   * Disconnect - NO dependencies
   */
  const disconnect = useCallback(() => {
    terminalLogger.info('Disconnecting terminal', configRef.current.sessionId);
    
    // Clear all timeouts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current);
      flushTimeoutRef.current = null;
    }
    
    // Stop heartbeat
    stopHeartbeat();
    
    // Close WebSocket with code 1000 (normal closure)
    if (webSocketRef.current) {
      if (webSocketRef.current.readyState === WebSocket.OPEN) {
        webSocketRef.current.close(1000, 'User disconnected');
      } else {
        webSocketRef.current.close();
      }
      webSocketRef.current = null;
    }
    
    setState({ type: 'disconnected', reason: 'User disconnected' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // NO dependencies!

  /**
   * Clear terminal - NO dependencies
   */
  const clear = useCallback(() => {
    if (terminalRef.current) {
      terminalRef.current.clear();
    }
  }, []); // NO dependencies!

  /**
   * Resize terminal - NO dependencies
   */
  const resize = useCallback((cols: number, rows: number) => {
    if (terminalRef.current) {
      terminalRef.current.resize(cols, rows);
      sendWebSocketMessage({ type: 'resize', cols, rows });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // NO dependencies but uses sendWebSocketMessage which is stable

  /**
   * Focus terminal - NO dependencies
   */
  const focus = useCallback(() => {
    if (terminalRef.current) {
      terminalRef.current.focus();
    }
  }, []); // NO dependencies!

  /**
   * Send input - NO dependencies
   */
  const sendInput = useCallback((data: string) => {
    sendWebSocketMessage({ type: 'input', data });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // NO dependencies but uses sendWebSocketMessage which is stable

  // Update handler refs on each render to avoid stale closures
  handleDataRef.current = (data: string) => {
    // Add data to buffer first
    inputBufferRef.current += data;
    
    // Clear existing timeout
    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current);
      flushTimeoutRef.current = null;
    }
    
    // Flush immediately for special characters (including the buffered content)
    if (data === '\r' || data === '\x03' || data === '\x04' || data === '\x1b') {
      flushInputBuffer();
    } else {
      // Buffer regular input for a bit longer to allow typing
      flushTimeoutRef.current = setTimeout(flushInputBuffer, 50);
    }
  };

  handleKeyEventRef.current = (event: KeyboardEvent) => {
    const isMac = navigator.platform.toLowerCase().includes('mac');
    
    if (event.ctrlKey && event.key === 'c' && !event.metaKey && !event.shiftKey) {
      sendWebSocketMessage({ type: 'input', data: '\x03' });
      return false;
    }
    if (event.ctrlKey && event.key === 'd' && !event.metaKey) {
      sendWebSocketMessage({ type: 'input', data: '\x04' });
      return false;
    }
    if ((isMac && event.metaKey && event.key === 'k') || (!isMac && event.ctrlKey && event.key === 'l')) {
      terminalRef.current?.clear();
      return false;
    }
    return true;
  };

  /**
   * Connect WebSocket on mount
   */
  useEffect(() => {
    // Start WebSocket connection immediately on mount
    connectWebSocket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  /**
   * Handle window resize
   */
  useEffect(() => {
    const handleResize = () => {
      if (fitAddonRef.current && terminalRef.current) {
        try {
          fitAddonRef.current.fit();
          const { cols, rows } = terminalRef.current;
          // Direct WebSocket send since we're in effect
          if (webSocketRef.current?.readyState === WebSocket.OPEN) {
            const message: WebSocketMessage = { type: 'resize', cols, rows };
            webSocketRef.current.send(JSON.stringify(message));
          }
        } catch (e) {
          terminalLogger.warn('Terminal resize failed', undefined, e);
        }
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []); // No dependencies - uses refs

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      terminalLogger.info('Terminal unmounting, cleaning up', configRef.current.sessionId);
      
      // Use disconnect method for proper cleanup
      disconnect();
      
      // Dispose terminal
      if (terminalRef.current) {
        terminalRef.current.dispose();
        terminalRef.current = null;
      }
      
      // Reset state
      initializedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // No dependencies - runs only on unmount

  // Methods object - all functions are stable with no dependencies
  const methods: TerminalMethods = {
    connect: connectWebSocket,
    disconnect,
    sendInput,
    clear,
    resize,
    focus
  };

  return {
    terminalRef: containerRefCallback,
    state,
    methods
  };
}